import styles from './BescheidModal.module.css';
import {
  SelectGroup,
  Checkbox,
  Spinner,
  Modal,
  Button,
  Stack
} from '@efast_public/fjd-component-library';
import { useEffect, useState } from 'react';
import DecideApplicationPostal from './DecideApplicationPostal';
import DecideApplicationDigital from './DecideApplicationDigital';
import { useSendMessage } from '../../api/hooks/useSendMessage';
import { useSubmissionsAsNachrichten } from '../../api/hooks/useSubmissionsAsNachrichten';
import { Bescheidstatus } from '../../api/models/Status/Bescheidstatus';
import { ConfirmAction } from '../../api/models/UploadFiles/ConfirmAction';
import {
  ActionType,
  FileUploadAction
} from '../../api/models/UploadFiles/FileUploadAction';
import { UploadFiles } from '../../api/models/UploadFiles/UploadFiles';
import UploadFilesConfirmationAlert from './UploadFilesConfirmationAlert';
import { NachrichtType } from '../../api/models/Nachrichten/NachrichtModel';
import {
  errorTextOnNotAvailableBundIdConnection,
  isApplicationInFederalNetwork
} from '../../shared/utils/mandant';
import { useNotification } from '../../shared/notification/useNotification';
import { useQuery } from '@tanstack/react-query';
import { CaseDetailsModel } from '../../api/models/Case/CaseDetailsModel';
import { BehApiRoutes } from '../../api/beh-api';
import { KommunikationRequestModel } from '../NachrichtSenden/KommunikationRequestModel';
import { useProfile } from '../../shared/hooks/AuthContext';

interface DecideApplicationModalProps {
  readonly closeOnSecondaryButtonClick: () => void;
  readonly onContinueButtonClick: (
    applicationIsPositiveDecidedVar: boolean
  ) => void;
  readonly isPositiveDecided?: boolean;
  readonly openAbschliessenModal: () => void;
  readonly isLoadingSend: boolean;
  readonly fileDispatch: (fileAction: FileUploadAction) => void;
  readonly filesUploadstate: UploadFiles;
  readonly confirmAction: ConfirmAction;
  readonly isOpen: boolean;
  readonly caseId: string;
}

export default function DecideApplicationModal({
  caseId,
  ...props
}: DecideApplicationModalProps) {
  const filesUploadstate: UploadFiles = props.filesUploadstate;
  const { data: caseDetails, refetch: refreshCaseDetails } = useQuery<
    CaseDetailsModel,
    Error
  >({
    queryKey: [BehApiRoutes.case(caseId ?? '')]
  });
  const { profile } = useProfile();
  const version = caseDetails?.version ?? '';
  const canCommunicate = caseDetails?.canCommunicate;
  const [sendDigitally, setSendDigitally] = useState<boolean>(false);
  const [isApplicationExaminedAndChecked, setIsApplicationExaminedAndChecked] =
    useState<boolean>(
      props.isPositiveDecided !== undefined ||
        filesUploadstate.additionalFiles.length > 0 ||
        filesUploadstate.bescheidUpload !== undefined
    );
  const [applicationIsPositiveDecided, setApplicationIsPositiveDecided] =
    useState<boolean | undefined>(props.isPositiveDecided);
  const [applicationIsFinished, setApplicationIsFinished] =
    useState<boolean>(false);

  const { notifyUser } = useNotification();
  const { sendMessage } = useSendMessage();

  const { refetch, error: useSubmissionsAsNachrichtenError } =
    useSubmissionsAsNachrichten({
      caseId
    });

  const sendBescheidPostalisch = () => {
    const payload: KommunikationRequestModel = {
      absender: `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`,
      betreff: '',
      nachrichttext: '',
      attachments: filesUploadstate.bescheidUpload
        ? [filesUploadstate.bescheidUpload, ...filesUploadstate.additionalFiles]
        : filesUploadstate.additionalFiles,
      nachrichtType: sendDigitally
        ? NachrichtType.BESCHEID_DIGITAL
        : NachrichtType.BESCHEID_POSTALISCH,
      bescheidstatus: applicationIsPositiveDecided
        ? Bescheidstatus.POSITIV
        : Bescheidstatus.NEGATIV,
      caseVersion: version
    };
    sendMessage({ payload, caseId: caseId })
      .then(() => refetch())
      .catch(() => {})
      .finally(() => {
        props.closeOnSecondaryButtonClick();
        refreshCaseDetails();
      });
  };

  useEffect(() => {
    if (!caseDetails?.isEBescheidConsentGiven) {
      setIsApplicationExaminedAndChecked(false);
      setApplicationIsPositiveDecided(undefined);
      setSendDigitally(false);
    }
  }, [caseDetails]);

  const onSendTypeSwitchChange = () => {
    props.fileDispatch({
      type: ActionType.REMOVE_ALL_FILES,
      onConfirmation: () => {
        setIsApplicationExaminedAndChecked(false);
        setApplicationIsPositiveDecided(undefined);
        setSendDigitally(!sendDigitally);
      }
    });
  };

  const onPrimaryClickPostal = () => {
    sendBescheidPostalisch();
    if (applicationIsFinished) {
      props.openAbschliessenModal();
    }
  };

  const getPrimaryButtonLabel = (): string => {
    if (props.confirmAction.toConfirm) {
      return props.confirmAction.buttonLabel;
    }

    if (sendDigitally) {
      return 'Weiter zum Bescheidversand';
    }

    if (applicationIsFinished) {
      return 'Abschließen und postalisch bescheiden';
    }

    return 'Postalisch bescheiden';
  };

  const getPrimaryButtonDisabled = (): boolean => {
    if (props.confirmAction.toConfirm) {
      return false;
    }

    if (!isApplicationExaminedAndChecked) {
      return true;
    }

    if (sendDigitally && filesUploadstate.bescheidUpload === undefined) {
      return true;
    }

    if (applicationIsPositiveDecided === undefined) {
      return true;
    }

    if (props.isLoadingSend) {
      return true;
    }

    return false;
  };

  const onPrimaryButtonClicked = () => {
    if (props.confirmAction.toConfirm) {
      props.confirmAction.onConfirmation();
      return;
    }

    if (sendDigitally) {
      if (isApplicationInFederalNetwork()) {
        props.onContinueButtonClick(applicationIsPositiveDecided!);
      } else {
        notifyUser({
          intent: 'error',
          title: errorTextOnNotAvailableBundIdConnection
        });
      }
    } else {
      onPrimaryClickPostal();
    }
  };

  const onSecondaryButtonClicked = () => {
    if (props.confirmAction.toConfirm) {
      props.confirmAction.onCancel();
      return;
    }

    props.closeOnSecondaryButtonClick();
    refreshCaseDetails();
  };

  const heading = props.confirmAction.toConfirm
    ? props.confirmAction.heading
    : 'Antrag bescheiden';

  return (
    <Modal
      appElement={document.querySelector('.fjd-base-layout') as HTMLElement}
      closeOnBackdropClick
      open={props.isOpen}
      onClose={() => props.closeOnSecondaryButtonClick()}
      data-testid="attachment-modal"
    >
      <Modal.Header
        id="DecideApplicationModal-header"
        onClose={props.closeOnSecondaryButtonClick}
      >
        {heading}
      </Modal.Header>
      <Modal.Content>
        {props.isLoadingSend ? (
          <Spinner />
        ) : props.confirmAction.toConfirm ? (
          <UploadFilesConfirmationAlert confirmAction={props.confirmAction} />
        ) : (
          <span className={styles.grey}>
            <Stack direction={'column'} spacing="3xl">
              <Stack direction={'column'}>
                {canCommunicate && caseDetails?.isEBescheidConsentGiven && (
                  <Stack direction={'row'}>
                    <SelectGroup
                      name="result"
                      selectType="single"
                      id="btn-select-group-bescheid"
                    >
                      <SelectGroup.Button
                        checked={sendDigitally}
                        value="digital-versenden"
                        label={'Bescheid digital versenden'}
                        onClick={() =>
                          !sendDigitally && onSendTypeSwitchChange()
                        }
                      />
                      <SelectGroup.Button
                        checked={!sendDigitally}
                        value="postal-versenden"
                        label={'Bescheid postalisch versenden'}
                        onClick={() =>
                          sendDigitally && onSendTypeSwitchChange()
                        }
                      />
                    </SelectGroup>
                  </Stack>
                )}
              </Stack>
              <Stack direction={'column'} spacing="sm">
                <h3>Bitte bestätigen Sie folgenden Hinweis</h3>
                <Stack direction={'row'}>
                  <Checkbox
                    id={'application_is_checked_checkbox'}
                    label={
                      'Der vorliegende Antrag ist vollständig geprüft und kann beschieden werden'
                    }
                    checked={isApplicationExaminedAndChecked}
                    onChange={() => {
                      if (isApplicationExaminedAndChecked) {
                        props.fileDispatch({
                          type: ActionType.REMOVE_ALL_FILES,
                          onConfirmation: () => {
                            setIsApplicationExaminedAndChecked(
                              !isApplicationExaminedAndChecked
                            );
                            setApplicationIsPositiveDecided(undefined);
                          }
                        });
                      } else {
                        setIsApplicationExaminedAndChecked(
                          !isApplicationExaminedAndChecked
                        );
                        setApplicationIsPositiveDecided(undefined);
                      }
                    }}
                  />
                </Stack>
              </Stack>
              {caseDetails?.isEBescheidConsentGiven &&
                sendDigitally &&
                !props.confirmAction.toConfirm && (
                  <DecideApplicationDigital
                    isApplicationExaminedAndChecked={
                      isApplicationExaminedAndChecked
                    }
                    setIsApplicationPositiveDecided={
                      setApplicationIsPositiveDecided
                    }
                    isApplicationPositiveDecided={applicationIsPositiveDecided}
                    fileDispatch={props.fileDispatch}
                    filesUploadstate={filesUploadstate}
                  />
                )}
              {(!caseDetails?.isEBescheidConsentGiven || !sendDigitally) &&
                !props.confirmAction.toConfirm && (
                  <DecideApplicationPostal
                    isApplicationExaminedAndChecked={
                      isApplicationExaminedAndChecked
                    }
                    setApplicationIsPositiveDecided={
                      setApplicationIsPositiveDecided
                    }
                    applicationIsPositiveDecided={applicationIsPositiveDecided}
                    fileDispatch={props.fileDispatch}
                    filesUploadstate={filesUploadstate}
                    isApplicationFinished={applicationIsFinished}
                    setApplicationIsFinished={setApplicationIsFinished}
                  />
                )}
            </Stack>
          </span>
        )}
      </Modal.Content>
      <Modal.Footer>
        {useSubmissionsAsNachrichtenError ? (
          <Button variant="primary" onClick={() => onSecondaryButtonClicked()}>
            Schließen
          </Button>
        ) : (
          <>
            <Button
              variant="secondary-outline"
              disabled={props.isLoadingSend}
              onClick={() => onSecondaryButtonClicked()}
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              disabled={getPrimaryButtonDisabled()}
              onClick={() => onPrimaryButtonClicked()}
            >
              {getPrimaryButtonLabel()}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
